import * as Sentry from '@sentry/nextjs';
import 'client-only';

import {
  DOCUMENT_UPLOAD_SOURCE_TAG_KEY,
  DOCUMENT_UPLOAD_TAG_KEY,
  DOCUMENT_UPLOAD_TAG_VALUE,
} from './constants';
import { DOCUMENT_UPLOAD_SENTRY_REPORTED } from './sentry-reported-marker';
import type { DocumentUploadTelemetryContext } from './types';

export { DOCUMENT_UPLOAD_SENTRY_REPORTED };

/**
 * Captures an upload failure to Sentry with per-upload tags and context,
 * inside an isolated forked scope so concurrent uploads cannot stomp on each
 * other's tags. Marks the error so the global TanStack handler skips it.
 *
 * Never throws.
 */
export function captureDocumentUploadErrorClient(
  context: DocumentUploadTelemetryContext,
  error: unknown,
  options: { httpStatus?: number } = {}
): void {
  try {
    if (!(error instanceof Error)) return;

    Sentry.withScope(scope => {
      scope.setTag(DOCUMENT_UPLOAD_TAG_KEY, DOCUMENT_UPLOAD_TAG_VALUE);
      scope.setTag(DOCUMENT_UPLOAD_SOURCE_TAG_KEY, context.source);
      if (typeof options.httpStatus === 'number') {
        scope.setTag('apiStatus', options.httpStatus);
      }
      scope.setContext('document_upload', {
        source: context.source,
        deal_id: context.dealId,
        package_id: context.packageId,
        flow_id: context.flowId,
        file_count: context.fileCount,
        total_bytes: context.totalBytes,
        files: context.files,
        pathname: context.pathname,
      });
      Sentry.captureException(error);
    });

    (error as unknown as Record<symbol, unknown>)[
      DOCUMENT_UPLOAD_SENTRY_REPORTED
    ] = true;
  } catch {
    // Observability must never interrupt uploads.
  }
}
